import React, { useEffect, useMemo, useState } from 'react';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebaseDDH';
import { MONO, SANS } from '../../constants/fonts';

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '80vh',
    background: '#fff',
    border: '1px solid #e5e5ea',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    fontFamily: SANS,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 20px 12px',
  },
  title: {
    fontFamily: MONO,
    fontSize: '1.05rem',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: MONO,
    fontSize: '0.95rem',
    color: '#222',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
  },
  close: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    lineHeight: 1,
    color: '#999',
    cursor: 'pointer',
  },
  search: {
    margin: '0 20px 12px',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  },
  list: {
    overflowY: 'auto',
    flex: 1,
    padding: '0 20px 20px',
  },
  listRow: {
    padding: '10px 12px',
    border: '1px solid #eee',
    borderRadius: 8,
    fontSize: '0.85rem',
    marginBottom: 8,
    cursor: 'pointer',
    background: '#fff',
  },
  listRowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  name: {
    fontWeight: 600,
    color: '#222',
  },
  meta: {
    color: '#888',
    fontSize: '0.78rem',
  },
  snippet: {
    color: '#888',
    fontSize: '0.78rem',
    marginTop: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  empty: {
    color: '#999',
    fontSize: '0.85rem',
    padding: '0 20px 20px',
  },
  detail: {
    padding: '0 20px 20px',
    overflowY: 'auto',
  },
  detailName: {
    fontFamily: MONO,
    fontSize: '1.1rem',
    marginBottom: 4,
  },
  detailMeta: {
    color: '#888',
    fontSize: '0.8rem',
    marginBottom: 16,
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    gap: 12,
  },
  fieldLabel: {
    color: '#888',
    flexShrink: 0,
  },
  fieldValue: {
    textAlign: 'right',
  },
  textBlock: {
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  textBlockLabel: {
    color: '#888',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 600,
    marginBottom: 6,
  },
  textBlockBody: {
    fontSize: '0.88rem',
    color: '#333',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
  },
  deleteButton: {
    marginTop: 20,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #f0c4c0',
    background: '#fff',
    color: '#c0392b',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  confirmBox: {
    marginTop: 20,
    padding: 12,
    borderRadius: 8,
    border: '1px solid #f0c4c0',
    background: '#fdf3f2',
  },
  confirmText: {
    fontSize: '0.85rem',
    color: '#8a2c22',
    marginBottom: 10,
  },
  confirmRow: {
    display: 'flex',
    gap: 8,
  },
  confirmDeleteButton: {
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#c0392b',
    color: '#fff',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#222',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
};

function formatDate(ts) {
  return ts ? ts.toDate().toLocaleString() : '—';
}

function DeleteEnquiryButton({ enquiry }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!confirming) {
    return (
      <button type="button" style={styles.deleteButton} onClick={() => setConfirming(true)}>
        Delete message
      </button>
    );
  }

  return (
    <div style={styles.confirmBox}>
      <div style={styles.confirmText}>Delete this message from "{enquiry.name}"? This can't be undone.</div>
      <div style={styles.confirmRow}>
        <button
          type="button"
          style={styles.confirmDeleteButton}
          disabled={deleting}
          onClick={async () => {
            setDeleting(true);
            await deleteDoc(doc(db, 'enquiries', enquiry.id));
          }}
        >
          {deleting ? 'Deleting…' : 'Yes, delete'}
        </button>
        <button type="button" style={styles.cancelButton} onClick={() => setConfirming(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function EnquiryDetail({ enquiry }) {
  return (
    <div style={styles.detail}>
      <div style={styles.detailName}>{enquiry.name}</div>
      <div style={styles.detailMeta}>{formatDate(enquiry.submittedAt)}</div>

      {enquiry.company && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Company</span>
          <span style={styles.fieldValue}>{enquiry.company}</span>
        </div>
      )}
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Email</span>
        <span style={styles.fieldValue}>{enquiry.email}</span>
      </div>
      {enquiry.phone && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Phone</span>
          <span style={styles.fieldValue}>{enquiry.countryCode} {enquiry.phone}</span>
        </div>
      )}
      {enquiry.preferredContact?.length > 0 && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Preferred contact</span>
          <span style={styles.fieldValue}>{enquiry.preferredContact.join(', ')}</span>
        </div>
      )}
      {enquiry.projectTypes?.length > 0 && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Looking for</span>
          <span style={styles.fieldValue}>{enquiry.projectTypes.join(', ')}</span>
        </div>
      )}

      {enquiry.description && (
        <div style={styles.textBlock}>
          <div style={styles.textBlockLabel}>Project</div>
          <div style={styles.textBlockBody}>{enquiry.description}</div>
        </div>
      )}
      {enquiry.questions && (
        <div style={styles.textBlock}>
          <div style={styles.textBlockLabel}>Questions</div>
          <div style={styles.textBlockBody}>{enquiry.questions}</div>
        </div>
      )}
      {enquiry.extra && (
        <div style={styles.textBlock}>
          <div style={styles.textBlockLabel}>Anything else</div>
          <div style={styles.textBlockBody}>{enquiry.extra}</div>
        </div>
      )}

      <DeleteEnquiryButton enquiry={enquiry} />
    </div>
  );
}

export default function EnquiriesModal({ enquiries, loading, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.name, e.company, e.email].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [enquiries, query]);

  const selected = enquiries.find((e) => e.id === selectedId);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          {selected ? (
            <button type="button" style={styles.backButton} onClick={() => setSelectedId(null)}>
              ← Messages
            </button>
          ) : (
            <div style={styles.title}>Messages</div>
          )}
          <button type="button" style={styles.close} aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>

        {selected ? (
          <EnquiryDetail enquiry={selected} />
        ) : (
          <>
            <input
              style={styles.search}
              type="text"
              placeholder="Search by name, company or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <div style={styles.list}>
              {loading && <div style={styles.empty}>Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div style={styles.empty}>{enquiries.length === 0 ? 'No messages yet.' : 'No matches.'}</div>
              )}
              {filtered.map((e) => (
                <div key={e.id} style={styles.listRow} onClick={() => setSelectedId(e.id)}>
                  <div style={styles.listRowTop}>
                    <span style={styles.name}>{e.name}</span>
                    <span style={styles.meta}>{formatDate(e.submittedAt)}</span>
                  </div>
                  <div style={styles.snippet}>{e.company || e.email}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
